import { ROUTE_PATHS } from "@/config/routes"

/**
 * Return URL after "Stop impersonating" must include the same path, query, and **hash**
 * (e.g. `/transfer/123#transfer_company`). Hash is easy to drop when only
 * `pathname` is stored; we also persist last transfer fragment in sessionStorage as a fallback.
 */
export const TRANSFER_WIZARD_FRAGMENT_SESSION_KEY = "lr_transfer_wizard_fragment_v1"

const TRANSFER_HASH_PATTERN =
  /^(?:transfer_property|transfer_resident|merge_property|split_property|transfer_company)$/
const DASHBOARD_TRANSFER_PATH_PATTERN = new RegExp(
  `^(${ROUTE_PATHS.DASHBOARD_TRANSFER}/[^/?#]+)(\\?[^#]*)?$`,
)

function readStoredTransferFragment(): string | null {
  if (typeof window === "undefined") return null
  try {
    const f = sessionStorage.getItem(TRANSFER_WIZARD_FRAGMENT_SESSION_KEY)
    return f && TRANSFER_HASH_PATTERN.test(f) ? f : null
  } catch {
    return null
  }
}

/**
 * If the user should land on `/transfer/:id#…` but the stored path lost the hash,
 * append the last known wizard fragment from this browser session.
 */
function ensureTransferWizardHash(path: string): string {
  if (!path || path.includes("#")) return path
  const m = path.match(DASHBOARD_TRANSFER_PATH_PATTERN)
  if (!m) return path
  const frag = readStoredTransferFragment()
  if (!frag) return path
  return `${m[1]}${m[2] ?? ""}#${frag}`
}

/** Save when mounting transfer wizard with a valid `#transfer_*` hash (see TransferProcessPage). */
export function persistTransferWizardFragmentForSession(fragment: string): void {
  if (typeof window === "undefined") return
  if (!TRANSFER_HASH_PATTERN.test(fragment)) return
  try {
    sessionStorage.setItem(TRANSFER_WIZARD_FRAGMENT_SESSION_KEY, fragment)
  } catch {
    /* ignore */
  }
}

export function getCurrentPathForImpersonateReturn(): string {
  if (typeof window === "undefined") return ROUTE_PATHS.DASHBOARD
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

/**
 * Normalize stored return path for SPA navigation after {@link endAdminImpersonation}.
 * Handles full URLs and restores `#transfer_*` when missing for `/transfer/:id`.
 */
export function normalizeImpersonateReturnPath(raw: string | null | undefined): string {
  let t = (raw ?? "").trim()
  if (!t) return ROUTE_PATHS.DASHBOARD

  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t)
      if (typeof window !== "undefined" && u.origin === window.location.origin) {
        t = `${u.pathname}${u.search}${u.hash}`
      } else {
        t = `${u.pathname}${u.search}${u.hash}`
      }
    } catch {
      return ROUTE_PATHS.DASHBOARD
    }
  }

  if (!t.startsWith("/")) {
    if (t === ROUTE_PATHS.DASHBOARD.replace(/^\//, "")) {
      return ROUTE_PATHS.DASHBOARD
    }
    t = `/${t.replace(/^\/+/, "")}`
  }

  return ensureTransferWizardHash(t)
}
